import { createAsyncThunk } from '@reduxjs/toolkit';
import { notification } from 'antd';
import { fetchApiAuth } from '../utils/api';
import {
    SitesResponseProps,
    SitesFilterPayload,
    LastBookedSitesResponseProps,
    SiteDetailResponseProps,
    SiteImage,
    AlarmCodesResponseProps,
    GenerateCodeResponseProps,
    UpdateAlarmCodesResponseProps,
    UpdateAlarmCodesPayload,
    AlarmCodeHistoryResponseProps,
    DoorCctvCodesResponseProps,
    DoorCctvCodeHistoryResponseProps,
} from './types';
import { handleError, makeUrl } from '../utils/helpers';

export const fetchSites = createAsyncThunk(
    'sites/fetchSites',
    async (payload: SitesFilterPayload, { rejectWithValue }) => {
        const url = makeUrl('/sites', payload);
        try {
            const response: SitesResponseProps = await fetchApiAuth({
                method: 'GET',
                url,
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const fetchLastBookedSites = createAsyncThunk(
    'sites/fetchLastBookedSites',
    async (_, { rejectWithValue }) => {
        try {
            const response: LastBookedSitesResponseProps = await fetchApiAuth({
                method: 'GET',
                url: '/sites/last-booked',
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const fetchSiteDetail = createAsyncThunk(
    'sites/fetchSiteDetail',
    async (encryptedId: string, { rejectWithValue }) => {
        try {
            const response: SiteDetailResponseProps = await fetchApiAuth({
                method: 'GET',
                url: `/sites/${encryptedId}`,
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const updateTwoManSiteNotes = createAsyncThunk(
    'sites/updateTwoManSiteNotes',
    async (
        { encryptedId, twoManSiteNotes }: { encryptedId: string; twoManSiteNotes: string },
        { rejectWithValue },
    ) => {
        try {
            const response = await fetchApiAuth({
                method: 'PATCH',
                url: `/sites/${encryptedId}/two-man-site-notes`,
                body: { twoManSiteNotes },
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const uploadSiteImage = createAsyncThunk(
    'sites/uploadSiteImage',
    async (
        { encryptedId, file, description }: { encryptedId: string; file: File; description?: string },
        { rejectWithValue },
    ) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            if (description) {
                formData.append('description', description);
            }
            const response: { data: { siteImage: SiteImage } } = await fetchApiAuth({
                method: 'POST',
                url: `/sites/${encryptedId}/images`,
                body: formData,
            });
            notification.success({
                message: 'Success',
                description: 'Image uploaded successfully.',
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const updateSiteImage = createAsyncThunk(
    'sites/updateSiteImage',
    async (
        { encryptedId, imageId, description }: { encryptedId: string; imageId: number; description: string | null },
        { rejectWithValue },
    ) => {
        try {
            const response: { data: { siteImage: { id: number; description: string | null } } } = await fetchApiAuth({
                method: 'PATCH',
                url: `/sites/${encryptedId}/images/${imageId}`,
                body: { description },
            });
            notification.success({
                message: 'Success',
                description: 'Image updated successfully.',
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const deleteSiteImage = createAsyncThunk(
    'sites/deleteSiteImage',
    async (
        { encryptedId, imageId }: { encryptedId: string; imageId: number },
        { rejectWithValue },
    ) => {
        try {
            await fetchApiAuth({
                method: 'DELETE',
                url: `/sites/${encryptedId}/images/${imageId}`,
            });
            notification.success({
                message: 'Success',
                description: 'Image deleted successfully.',
            });
            return { imageId };
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const fetchAlarmCodes = createAsyncThunk(
    'sites/fetchAlarmCodes',
    async (encryptedId: string, { rejectWithValue }) => {
        try {
            const response: AlarmCodesResponseProps = await fetchApiAuth({
                method: 'GET',
                url: `/sites/${encryptedId}/alarm-codes`,
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const generateAlarmCode = createAsyncThunk(
    'sites/generateAlarmCode',
    async (
        { encryptedId, digitLength }: { encryptedId: string; digitLength: number },
        { rejectWithValue },
    ) => {
        try {
            const response: GenerateCodeResponseProps = await fetchApiAuth({
                method: 'POST',
                url: `/sites/${encryptedId}/alarm-codes/generate`,
                body: { digitLength },
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const updateAlarmCodes = createAsyncThunk(
    'sites/updateAlarmCodes',
    async (
        {
            encryptedId,
            userCode,
            managerCode,
            engineerCode,
            userCodePlain,
            managerCodePlain,
            engineerCodePlain,
        }: UpdateAlarmCodesPayload,
        { rejectWithValue },
    ) => {
        try {
            // Generated codes are sent back encrypted; manually typed codes are sent as plaintext
            const body: Omit<UpdateAlarmCodesPayload, 'encryptedId'> = {};
            if (userCode !== undefined) body.userCode = userCode;
            if (managerCode !== undefined) body.managerCode = managerCode;
            if (engineerCode !== undefined) body.engineerCode = engineerCode;
            if (userCodePlain !== undefined) body.userCodePlain = userCodePlain;
            if (managerCodePlain !== undefined) body.managerCodePlain = managerCodePlain;
            if (engineerCodePlain !== undefined) body.engineerCodePlain = engineerCodePlain;
            const response: UpdateAlarmCodesResponseProps = await fetchApiAuth({
                method: 'PUT',
                url: `/sites/${encryptedId}/alarm-codes`,
                body,
            });
            notification.success({
                message: 'Success',
                description: 'Alarm codes updated successfully.',
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const fetchAlarmCodeHistory = createAsyncThunk(
    'sites/fetchAlarmCodeHistory',
    async (
        { encryptedId }: { encryptedId: string },
        { rejectWithValue },
    ) => {
        try {
            const response: AlarmCodeHistoryResponseProps = await fetchApiAuth({
                method: 'GET',
                url: `/sites/${encryptedId}/alarm-codes/history`,
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const fetchDoorCctvCodes = createAsyncThunk<
    DoorCctvCodesResponseProps['data'],
    string,
    { rejectValue: string }
>(
    'sites/fetchDoorCctvCodes',
    async (encryptedId: string, { rejectWithValue }) => {
        try {
            const response: DoorCctvCodesResponseProps = await fetchApiAuth({
                method: 'GET',
                url: `/sites/${encryptedId}/door-cctv-codes`,
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const fetchDoorCctvCodeHistory = createAsyncThunk<
    DoorCctvCodeHistoryResponseProps['data'],
    { encryptedId: string },
    { rejectValue: string }
>(
    'sites/fetchDoorCctvCodeHistory',
    async ({ encryptedId }, { rejectWithValue }) => {
        try {
            const response: DoorCctvCodeHistoryResponseProps = await fetchApiAuth({
                method: 'GET',
                url: `/sites/${encryptedId}/door-cctv-codes/history`,
            });
            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);
